/**
 * Artifact update overlay — the rich, in-flight progress surface shown while
 * an `update_artifact` stream is running.
 *
 * Replaces the old tiny floating "Updating…" pill (see issue #961). Instead of
 * a subtle tag, this covers the canvas with a focused "updating" view:
 *   - an indeterminate progress bar + spinner so the operation never looks
 *     stuck,
 *   - a live metric (words / rows / size) and elapsed timer that prove the
 *     stream is actively producing content,
 *   - a live preview of the incoming body for documents (markdown is
 *     readable as it streams). For html/presentation/spreadsheet kinds the
 *     overlay shows only the spinner — users don't think of their dashboards
 *     in markup, and watching raw HTML/CSS scroll past reads as "broken".
 *
 * The underlying canvas keeps the previous body mounted behind this overlay,
 * so the existing finish→diff-highlight handoff is unaffected: on completion
 * the overlay unmounts and the canvas swaps to the new body with its change
 * bands. Self-gating — renders nothing unless an update is in flight, so each
 * canvas can mount it unconditionally.
 *
 * @module components/artifacts/artifact-update-overlay
 */

import { AnimatePresence, motion } from "framer-motion";
import { Loader2, Sparkles, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { ArtifactKind } from "@taqat/core/types";
import { MarkdownRenderer } from "@/components/file-preview/renderers/markdown-renderer";
import { Button } from "@/components/ui/button";
import { useArtifactStore } from "@/store/artifact-store";

/**
 * Full-canvas overlay driven entirely by the artifact store. Mount it once per
 * canvas; it shows itself only while `pendingContent` is non-null (an update
 * stream is in flight).
 */
export function ArtifactUpdateOverlay() {
  const pendingContent = useArtifactStore((s) => s.artifact.pendingContent);
  const kind = useArtifactStore((s) => s.artifact.kind);
  const title = useArtifactStore((s) => s.artifact.title);
  const hideArtifact = useArtifactStore((s) => s.hideArtifact);

  const isUpdateInFlight = pendingContent !== null;

  // Elapsed timer — starts when an update begins, resets when it ends. Ticking
  // proves activity even before the first delta lands.
  const [elapsedMs, setElapsedMs] = useState(0);
  const startedAtRef = useRef<number | null>(null);
  useEffect(() => {
    if (!isUpdateInFlight) {
      startedAtRef.current = null;
      setElapsedMs(0);
      return;
    }
    startedAtRef.current = Date.now();
    setElapsedMs(0);
    const id = window.setInterval(() => {
      if (startedAtRef.current !== null) setElapsedMs(Date.now() - startedAtRef.current);
    }, 250);
    return () => window.clearInterval(id);
  }, [isUpdateInFlight]);

  const metric = useMemo(
    () => streamMetric(kind, pendingContent ?? ""),
    [kind, pendingContent],
  );

  return (
    <AnimatePresence>
      {isUpdateInFlight ? (
        <motion.div
          key="artifact-update-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="absolute inset-0 z-30 flex flex-col bg-background/95 backdrop-blur-sm"
        >
          {/* Indeterminate progress bar pinned to the very top. */}
          <div className="relative h-1 w-full shrink-0 overflow-hidden bg-primary/15">
            <motion.div
              className="absolute inset-y-0 w-1/3 rounded-full bg-primary"
              animate={{ x: ["-110%", "330%"] }}
              transition={{ duration: 1.1, ease: "easeInOut", repeat: Infinity }}
            />
          </div>

          {/* Status header — spinner + label on the left, metric on the right. */}
          <div className="flex shrink-0 items-center gap-3 border-b bg-background/60 px-4 py-3">
            <Loader2 className="size-4 shrink-0 animate-spin text-primary" />
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold">
                Updating {kindNoun(kind)}…
              </div>
              <div className="truncate text-xs text-muted-foreground">
                {title || "Untitled"}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2 text-xs tabular-nums text-muted-foreground">
              {metric ? <span>{metric}</span> : null}
              <span aria-hidden className="opacity-40">
                ·
              </span>
              <span>{formatElapsed(elapsedMs)}</span>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-7 shrink-0"
              onClick={hideArtifact}
              title="Hide while it updates"
            >
              <X className="size-4" />
            </Button>
          </div>

          {/* Live preview — the new body streaming in. */}
          <div className="relative min-h-0 flex-1 overflow-hidden">
            <UpdatePreview kind={kind} content={pendingContent ?? ""} />
            <div className="pointer-events-none absolute right-3 top-3 flex items-center gap-1 rounded-full border bg-background/90 px-2.5 py-1 text-[11px] font-medium text-muted-foreground shadow-sm backdrop-blur">
              <Sparkles className="size-3 text-primary" />
              Live draft
            </div>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

/**
 * Renders the streaming body per kind. Documents render as live markdown so the
 * user reads the rewrite as prose. Other kinds (html / presentation /
 * spreadsheet) deliberately do NOT show the streaming raw source — users
 * don't think of their dashboards/decks/sheets as HTML, and watching markup
 * scroll by reads as "broken" rather than "progressing". The header above
 * already shows a spinner + size metric + elapsed timer + animated progress
 * bar, which is enough activity signal. The final body swaps in atomically
 * on finish.
 */
function UpdatePreview({ kind, content }: { kind: ArtifactKind; content: string }) {
  if (kind === "document") {
    if (content.length === 0) return <PreviewPlaceholder />;
    return <MarkdownStream content={content} />;
  }
  return <PreviewPlaceholder />;
}

/**
 * Live markdown preview that auto-follows the stream. `MarkdownRenderer`
 * forwards its ref to the content node inside a Radix `ScrollArea`, so we walk
 * up to the viewport and pin it to the bottom as new deltas land.
 */
function MarkdownStream({ content }: { content: string }) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const viewport = ref.current?.closest<HTMLElement>(
      "[data-radix-scroll-area-viewport]",
    );
    if (viewport) viewport.scrollTop = viewport.scrollHeight;
  }, [content]);
  return (
    <MarkdownRenderer ref={ref} content={content} className="h-full border-0 bg-transparent" />
  );
}

/** Shown before the first delta arrives so the area never looks empty. */
function PreviewPlaceholder() {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 text-muted-foreground">
      <Loader2 className="size-7 animate-spin text-primary" />
      <p className="text-sm">Thinking through the changes…</p>
    </div>
  );
}

/** Human-friendly noun for the kind, used in the "Updating {noun}…" label. */
function kindNoun(kind: ArtifactKind): string {
  switch (kind) {
    case "presentation":
      return "deck";
    case "spreadsheet":
      return "spreadsheet";
    case "html":
      return "page";
    case "image":
      return "image";
    case "document":
    default:
      return "document";
  }
}

/**
 * A live, kind-appropriate measure of how much has streamed so far. Returns an
 * empty string when there's nothing meaningful to show yet.
 */
function streamMetric(kind: ArtifactKind, content: string): string {
  if (content.length === 0) return "";
  switch (kind) {
    case "spreadsheet": {
      const rows = content.split(/\r?\n/).filter((line) => line.trim().length > 0).length;
      const data = Math.max(0, rows - 1); // first line is the header
      return `${data.toLocaleString()} ${data === 1 ? "row" : "rows"}`;
    }
    case "html": {
      const kb = content.length / 1024;
      return kb < 1 ? `${content.length} chars` : `${kb.toFixed(1)} KB`;
    }
    case "document":
    case "presentation":
    default: {
      const words = content.trim().split(/\s+/).filter(Boolean).length;
      return `${words.toLocaleString()} ${words === 1 ? "word" : "words"}`;
    }
  }
}

/** Formats elapsed milliseconds as `m:ss`. */
function formatElapsed(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}
